"use client";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { currentStatus as data } from "@/lib/data/status";
import { CheckCircle2 } from "lucide-react";

const accent = "var(--color-emerald)";

const ServiceTable = ({ title, services }: { title: string; services: { service: string; status: string; latency: string; uptime: string }[] }) => (
  <Reveal>
    <h3 className="text-lg mt-10 mb-3">{title}</h3>
    <div className="glass rounded-2xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[rgb(var(--border)/0.18)] bg-[rgb(var(--border)/0.04)]">
              <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))]">Service</th>
              <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))]">Status</th>
              <th className="text-right px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))] hidden md:table-cell">Latency</th>
              <th className="text-right px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))] hidden md:table-cell">Uptime</th>
            </tr>
          </thead>
          <tbody>
            {services.map((s) => (
              <tr key={s.service} className="border-b border-[rgb(var(--border)/0.08)]">
                <td className="px-4 py-3 align-top font-medium">{s.service}</td>
                <td className="px-4 py-3 align-top">
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold" style={{ color: accent }}>
                    <CheckCircle2 className="h-3 w-3" /> {s.status}
                  </span>
                </td>
                <td className="px-4 py-3 align-top text-right hidden md:table-cell font-mono text-xs">{s.latency}</td>
                <td className="px-4 py-3 align-top text-right hidden md:table-cell font-mono text-xs" style={{ color: accent }}>{s.uptime}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </Reveal>
);

export function CurrentStatus() {
  return (
    <Section id="current-status" className="relative">
      <Container>
        <Reveal>
          <div className="text-center max-w-3xl mx-auto mb-10">
            <span className="eyebrow-tiny text-[var(--color-emerald)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{data.intro}</p>
            <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full glass">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: accent }} />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ background: accent }} />
              </span>
              <span className="text-sm font-semibold" style={{ color: accent }}>{data.overall}</span>
            </div>
          </div>
        </Reveal>
        <ServiceTable title="Core Platform Services" services={data.coreServices} />
        <ServiceTable title="AI Engine Services" services={data.aiEngineServices} />
        <ServiceTable title="Infrastructure Services" services={data.infraServices} />
        <Reveal delay={200}>
          <p className="mt-6 text-xs text-center text-[rgb(var(--fg-subtle))] font-mono">{data.lastUpdated}</p>
        </Reveal>
      </Container>
    </Section>
  );
}
